import { Fragment, useEffect, useMemo, useRef, useState } from 'react';
import { Header, Label } from 'semantic-ui-react';
import { ExtractionSpecType, HighlightSpecType } from '../types';
import { getPageExtractions } from '../utils/pdfPageUtils';
import PDFPage from '../pdfPage';

type MultiPageViewPropsType = {
    numberOfPages: number;
    handleExtraction: (extractionSpec: ExtractionSpecType) => void;
    selectionDisabled: boolean;
    extractionSpecs: HighlightSpecType[];
    currentPage: number;
    scale: number;
    // Optional subset of 1-based page numbers to render. Defaults to every page.
    pages?: number[];
};

const MultiPageView = ({
    numberOfPages,
    handleExtraction,
    selectionDisabled,
    extractionSpecs,
    currentPage,
    scale,
    pages,
}: MultiPageViewPropsType) => {
    // Count of successfully rendered pages - used to track loading progress
    // This is crucial for knowing when all pages are ready for navigation
    const [pagesRendered, setPagesRendered] = useState(0);

    // Store DOM references to each page using a Map
    const pageRefs = useRef(new Map<number, HTMLDivElement | null>());

    // The pages actually rendered: an explicit subset when provided, otherwise the whole document.
    const pagesToRender = useMemo(() => {
        return pages ?? Array.from({ length: numberOfPages }, (_, i) => i + 1);
    }, [pages, numberOfPages]);

    // Check if all pages are rendered - this controls when navigation becomes enabled.
    // Compare against the number of pages we actually render (not the document total), or
    // scroll-to-page would never fire when rendering a subset.
    const areAllPagesRendered = useMemo(() => {
        return pagesRendered === pagesToRender.length;
    }, [pagesRendered, pagesToRender]);

    // Called when each page finishes rendering its visual content
    // We increment a counter to track progress and determine when all pages are ready
    const onPageRender = () => {
        setPagesRendered((prev) => prev + 1);
    };

    // Creates a ref callback for a specific page that handles both assignment and cleanup
    const setPageRef = (page: number) => {
        // Return a ref callback function that React will call with the DOM element

        return (el: HTMLDivElement | null) => {
            // Store the element reference in our Map, keyed by page number
            // This happens when the component mounts or updates
            pageRefs.current.set(page, el);
            // Return a cleanup function that React will call when the component unmounts
            // This ensures we don't keep references to elements that no longer exist
            return () => {
                // Remove this page's reference from the Map when the component unmounts

                pageRefs.current.delete(page);
            };
        };
    };

    useEffect(() => {
        if (!areAllPagesRendered) {
            return;
        }

        const element = pageRefs.current.get(currentPage);

        if (!element) {
            return;
        }

        // THE CRITICAL PART: Proper timing for scroll operations
        // Use requestAnimationFrame for optimal timing with browser rendering cycle
        // This ensures we're in sync with the browser's rendering pipeline
        requestAnimationFrame(() => {
            // Use a nested requestAnimationFrame to ensure we're safely after the next repaint
            // This double-rAF approach gives the browser enough time to:
            // 1. Complete the previous render cycle
            // 2. Calculate all layout dimensions
            // 3. Apply all styles
            // 4. Before we attempt to scroll
            requestAnimationFrame(() => {
                element.scrollIntoView({
                    block: 'start',
                });
            });
        });
    }, [currentPage, areAllPagesRendered]);

    return (
        <>
            {pagesToRender.map((currentPageNumber) => {
                return (
                    <Fragment key={currentPageNumber}>
                        <div ref={setPageRef(currentPageNumber)}>
                            <PDFPage
                                key={currentPageNumber}
                                page={currentPageNumber}
                                handleExtraction={handleExtraction}
                                pendingPageExtractionSpecs={getPageExtractions(currentPageNumber, extractionSpecs)}
                                selectionDisabled={selectionDisabled}
                                onRenderSuccess={onPageRender}
                                scale={scale}
                            />
                        </div>
                        <Header>
                            <Label size="big">Page {currentPageNumber}</Label>
                        </Header>
                    </Fragment>
                );
            })}
        </>
    );
};

export default MultiPageView;
